import React from 'react';
import { Box, Typography } from '@mui/material';
import { DragIndicator } from '@mui/icons-material';
import { CatalogNode, CatalogNodeType } from './catalogDoc';
import { CatalogPort, portsForNode } from './catalogPorts';
import { CATEGORY_COLOR, catalogNodeTitle } from './catalogPreview';
import { useI18n } from '../i18n';

export { CATEGORY_COLOR, catalogNodeTitle, palettePreviewNode } from './catalogPreview';
export type { PalettePayload } from './catalogPreview';

export const CatalogBlock: React.FC<{
  node: CatalogNode;
  palette?: boolean;
  renderPort?: (port: CatalogPort | undefined, side: 'left' | 'right') => React.ReactNode;
}> = ({ node, palette, renderPort }) => {
  const { t } = useI18n();
  const color = CATEGORY_COLOR[node.type] || '#546e7a';
  const ports = portsForNode(node.type, {
    poll: node.poll,
    widgetType: node.widgetType,
    busType: node.busType,
    role: node.role,
    deviceType: node.deviceType,
    channelNames: node.channelNames,
    channelReadOnly: node.channelReadOnly,
    icon: node.icon,
  });
  const ins = ports.filter((port) => port.dir === 'in');
  const outs = ports.filter((port) => port.dir === 'out');
  const rows = Math.max(ins.length, outs.length, 1);
  const slot = renderPort || ((port, side) => <StaticPort port={port} side={side} t={t} />);
  return (
    <Box
      className="catalog-block"
      sx={{
        minWidth: palette ? 0 : 148,
        width: palette ? '100%' : 'auto',
        maxWidth: palette ? '100%' : 260,
        borderRadius: 1,
        border: `1px solid ${color}`,
        bgcolor: node.type === 'dashboard' ? 'rgba(106,27,154,0.06)' : 'background.paper',
        boxShadow: palette ? 2 : 1,
        userSelect: 'none',
        transition: 'box-shadow 120ms ease, transform 120ms ease',
      }}
    >
      <Box sx={{ px: 1, py: 0.5, bgcolor: color, color: '#fff', position: 'relative', pr: palette ? 2.5 : 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.2 }}>
          {t(`flow.brick.${node.type as CatalogNodeType}`)}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, lineHeight: 1.2 }}>
          {catalogNodeTitle(node, t)}
        </Typography>
        {palette && (
          <DragIndicator sx={{ position: 'absolute', right: 2, top: 4, fontSize: 16, opacity: 0.85 }} />
        )}
      </Box>
      <Box sx={{ py: 0.5 }}>
        {Array.from({ length: rows }).map((_, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', minHeight: 18, px: 0.5 }}>
            {slot(ins[index], 'left')}
            <Box sx={{ flex: 1 }} />
            {slot(outs[index], 'right')}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

function StaticPort({
  port,
  side,
  t,
}: {
  port?: CatalogPort;
  side: 'left' | 'right';
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  if (!port) {
    return <Box sx={{ width: 8 }} />;
  }
  return (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      flexDirection: side === 'right' ? 'row-reverse' : 'row',
    }}>
      <Box sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: 'grey.500',
        flexShrink: 0,
      }} />
      <Typography variant="caption" color="text.secondary">
        {port.label || t(`flow.port.${port.labelKey}`, port.n != null ? { n: port.n } : undefined)}
      </Typography>
    </Box>
  );
}
